/**
 * Game Versions Configuration
 *
 * This file contains configuration for all supported game systems and their versions.
 * Each game system has editions/versions with information about API availability and implementation status.
 * This is a central configuration used throughout the application.
 */

// Game System Types (for categorization and filtering)
export const GAME_SYSTEMS = {
  DND: 'dungeons-and-dragons',
  PATHFINDER: 'pathfinder',
  STARFINDER: 'starfinder',
  CALL_OF_CTHULHU: 'call-of-cthulhu',
  WARHAMMER: 'warhammer',
  CUSTOM: 'custom'
}

/**
 * Complete list of all game versions supported in the application
 *
 * Structure:
 * - id: Unique identifier for the version
 * - displayName: Name shown in UI
 * - gameSystem: The parent game system this belongs to
 * - apiStatus: API implementation status (ACTIVE, PARTIAL, PLANNED, NONE)
 * - apiBaseUrl: URL for the API if available
 * - apiSpecPath: Path to OpenAPI spec file if available
 * - releaseYear: Year this game edition was released
 * - description: Brief description
 * - iconPath: Path to icon image
 * - sortOrder: Controls order in dropdowns (lower numbers first)
 * - hasCharacterSheet: Whether a complete character sheet implementation exists
 */
export const GAME_VERSIONS = {
  // D&D Editions
  DND_5E: {
    id: 'dnd_5e',
    displayName: 'D&D 5e (5.1)',
    gameSystem: GAME_SYSTEMS.DND,
    apiStatus: 'ACTIVE',
    apiBaseUrl: 'https://www.dnd5eapi.co/api/2014',
    apiSpecPath: '/src/services/api/specs/dnd5e-openapi.json',
    releaseYear: 2014,
    description: 'Dungeons & Dragons 5th Edition (2014)',
    iconPath: '/icons/dnd5e-logo.png',
    sortOrder: 10,
    hasCharacterSheet: true,
    isDefault: true,
    code: '5e' // short code used for firestore path like rules/5e
  },
  DND_4E: {
    id: 'dnd_4e',
    displayName: 'D&D 4th Edition',
    gameSystem: GAME_SYSTEMS.DND,
    apiStatus: 'PLANNED',
    apiBaseUrl: null,
    apiSpecPath: null,
    releaseYear: 2008,
    description: 'Dungeons & Dragons 4th Edition (2008)',
    iconPath: '/icons/dnd4e-logo.png',
    sortOrder: 20,
    hasCharacterSheet: false
  },
  DND_3_5E: {
    id: 'dnd_3_5e',
    displayName: 'D&D 3.5 Edition',
    gameSystem: GAME_SYSTEMS.DND,
    apiStatus: 'PLANNED',
    apiBaseUrl: null,
    apiSpecPath: null,
    releaseYear: 2003,
    description: 'Dungeons & Dragons 3.5 Edition (2003)',
    iconPath: '/icons/dnd35e-logo.png',
    sortOrder: 30,
    hasCharacterSheet: false
  },
  DND_3E: {
    id: 'dnd_3e',
    displayName: 'D&D 3rd Edition',
    gameSystem: GAME_SYSTEMS.DND,
    apiStatus: 'NONE',
    apiBaseUrl: null,
    apiSpecPath: null,
    releaseYear: 2000,
    description: 'Dungeons & Dragons 3rd Edition (2000)',
    iconPath: '/icons/dnd3e-logo.png',
    sortOrder: 40,
    hasCharacterSheet: false
  },
  DND_2E: {
    id: 'dnd_2e',
    displayName: 'D&D 2nd Edition (AD&D)',
    gameSystem: GAME_SYSTEMS.DND,
    apiStatus: 'NONE',
    apiBaseUrl: null,
    apiSpecPath: null,
    releaseYear: 1989,
    description: 'Advanced Dungeons & Dragons 2nd Edition (1989)',
    iconPath: '/icons/dnd2e-logo.png',
    sortOrder: 50,
    hasCharacterSheet: false
  },
  DND_1E: {
    id: 'dnd_1e',
    displayName: 'D&D 1st Edition (AD&D)',
    gameSystem: GAME_SYSTEMS.DND,
    apiStatus: 'NONE',
    apiBaseUrl: null,
    apiSpecPath: null,
    releaseYear: 1977,
    description: 'Advanced Dungeons & Dragons 1st Edition (1977)',
    iconPath: '/icons/dnd1e-logo.png',
    sortOrder: 60,
    hasCharacterSheet: false
  },
  DND_BASIC: {
    id: 'dnd_basic',
    displayName: 'D&D Basic/Expert',
    gameSystem: GAME_SYSTEMS.DND,
    apiStatus: 'NONE',
    apiBaseUrl: null,
    apiSpecPath: null,
    releaseYear: 1981,
    description: 'Dungeons & Dragons Basic/Expert Sets (1981)',
    iconPath: '/icons/dnd-basic-logo.png',
    sortOrder: 70,
    hasCharacterSheet: false
  },
  DND_OD: {
    id: 'dnd_od',
    displayName: 'Original D&D',
    gameSystem: GAME_SYSTEMS.DND,
    apiStatus: 'NONE',
    apiBaseUrl: null,
    apiSpecPath: null,
    releaseYear: 1974,
    description: 'Original Dungeons & Dragons (1974)',
    iconPath: '/icons/dnd-original-logo.png',
    sortOrder: 80,
    hasCharacterSheet: false
  },

  // World of Darkness
  WORLD_OF_DARKNESS: {
    id: 'world_of_darkness',
    displayName: 'World of Darkness',
    gameSystem: GAME_SYSTEMS.CUSTOM,
    apiStatus: 'PARTIAL',
    apiBaseUrl: null,
    apiSpecPath: null,
    releaseYear: 1991,
    description: 'World of Darkness (Vampire: The Masquerade)',
    iconPath: '/icons/wod-logo.png',
    sortOrder: 200,
  hasCharacterSheet: true,
  code: 'WoD'
  },

  // Pathfinder Editions
  PATHFINDER_2E: {
    id: 'pathfinder_2e',
    displayName: 'Pathfinder 2nd Edition',
    gameSystem: GAME_SYSTEMS.PATHFINDER,
    apiStatus: 'PLANNED',
    apiBaseUrl: null,
    apiSpecPath: null,
    releaseYear: 2019,
    description: 'Pathfinder 2nd Edition (2019)',
    iconPath: '/icons/pathfinder2e-logo.png',
    sortOrder: 100,
    hasCharacterSheet: false
  },
  PATHFINDER_1E: {
    id: 'pathfinder_1e',
    displayName: 'Pathfinder 1st Edition',
    gameSystem: GAME_SYSTEMS.PATHFINDER,
    apiStatus: 'PLANNED',
    apiBaseUrl: null,
    apiSpecPath: null,
    releaseYear: 2009,
    description: 'Pathfinder 1st Edition (2009)',
    iconPath: '/icons/pathfinder1e-logo.png',
    sortOrder: 110,
    hasCharacterSheet: false
  }

  // Other planned game systems can be added here
}

/**
 * API implementation status codes
 */
export const API_STATUS = {
  ACTIVE: 'ACTIVE', // Fully implemented API
  PARTIAL: 'PARTIAL', // Partial implementation
  PLANNED: 'PLANNED', // Planned for future implementation
  NONE: 'NONE' // No implementation planned
}

/**
 * Get a color for an API status (for UI display)
 * @param {string} status - The API status
 * @returns {string} Color name for the status
 */
export function getApiStatusColor(status) {
  switch (status) {
    case API_STATUS.ACTIVE:
      return 'positive'
    case API_STATUS.PARTIAL:
      return 'info'
    case API_STATUS.PLANNED:
      return 'warning'
    case API_STATUS.NONE:
    default:
      return 'grey-7'
  }
}

/**
 * Get an array of all game versions for use in dropdowns
 * Sorts by sortOrder property
 *
 * @param {boolean} includeAll - Whether to include all versions or only those with some API status
 * @param {string} gameSystem - Filter by game system (optional)
 * @param {boolean} onlyWithCharacterSheets - Filter to only include versions with character sheets
 * @param {boolean} onlyCompleteAPI - Whether to only include versions with ACTIVE API status
 * @returns {Array} Array of game version objects formatted for dropdowns
 */
export function getAllGameVersions(
  includeAll = true,
  gameSystem = null,
  onlyWithCharacterSheets = false,
  onlyCompleteAPI = false
) {
  return Object.values(GAME_VERSIONS)
    .filter((version) => {
      // Filter by game system if specified
      if (gameSystem && version.gameSystem !== gameSystem) return false

      // Filter by character sheet implementation if specified
      if (onlyWithCharacterSheets && !version.hasCharacterSheet) return false

      // Filter by complete API implementation if specified
      if (onlyCompleteAPI && version.apiStatus !== API_STATUS.ACTIVE) return false

      // Include all or filter by API status
      return includeAll || version.apiStatus !== API_STATUS.NONE
    })
    .sort((a, b) => a.sortOrder - b.sortOrder)
    .map((version) => ({
      id: version.id,
      name: version.displayName,
      description: version.description,
      disabled: version.apiStatus === API_STATUS.NONE || version.apiStatus === API_STATUS.PLANNED,
      apiStatus: version.apiStatus,
      iconPath: version.iconPath,
      hasApiSpec: !!version.apiSpecPath,
      hasCharacterSheet: version.hasCharacterSheet
    }))
}

/**
 * Get an array of game versions with active API implementations
 * @returns {Array} Array of game version objects with active APIs
 */
export function getActiveApiVersions() {
  return Object.values(GAME_VERSIONS)
    .filter(
      (version) =>
        version.apiStatus === API_STATUS.ACTIVE || version.apiStatus === API_STATUS.PARTIAL
    )
    .sort((a, b) => a.sortOrder - b.sortOrder)
    .map((version) => ({
      id: version.id,
      name: version.displayName,
      description: version.description,
      apiStatus: version.apiStatus
    }))
}

/**
 * Get a game version by ID
 * @param {string} id - The game version ID
 * @returns {Object|null} The game version object or null if not found
 */
export function getGameVersionById(id) {
  return Object.values(GAME_VERSIONS).find((version) => version.id === id) || null
}

/**
 * Get the short code for a game version used for concise Firestore paths
 * Falls back to '5e' (default edition) if not defined.
 * @param {string} id
 * @returns {string}
 */
export function getGameVersionCode(id) {
  const version = getGameVersionById(id) || getDefaultGameVersion()
  return version.code || '5e'
}

/**
 * Get the default game version
 * @returns {Object} The default game version
 */
export function getDefaultGameVersion() {
  return GAME_VERSIONS.DND_5E
}

/**
 * Check if a game version has an API spec
 * @param {string} versionId - The game version ID
 * @returns {boolean} True if the version has an API spec
 */
export function hasApiSpec(versionId) {
  const version = getGameVersionById(versionId)
  return version && !!version.apiSpecPath
}

export default {
  GAME_SYSTEMS,
  GAME_VERSIONS,
  API_STATUS,
  getAllVersions: getAllGameVersions,
  getActiveVersions: getActiveApiVersions,
  getVersionById: getGameVersionById,
  getVersionCode: getGameVersionCode,
  getDefault: getDefaultGameVersion,
  hasApiSpec,
  getApiStatusColor
}
